import uuid from 'uuid'
import * as logger from '@/utils/logger'

export const namespaced = true

const FLEX_AUDIO_WARNING_KEY = 'flex-audio-user-interaction-warning'
const FLEX_AUDIO_WARNING_MESSAGE =
  'You will not hear ringing notification. To resolve, go to Twilio Flex and click anywhere.'

export const state = {
  showFlexFrame: false,
  flexCommand: undefined,
  flexCommandTrigger: uuid.v4(),
  agentStatus: 'Unavailable',
  isOnCall: false,
  confirmCall: false,
  outgoingCallDetail: undefined,
  hasUserInteraction: false,
}

export const getters = {
  showFlexFrame() {
    return state.showFlexFrame
  },
  flexCommand() {
    return state.flexCommand
  },
  flexCommandTrigger() {
    return state.flexCommandTrigger
  },
  agentStatus() {
    return state.agentStatus
  },
  isOnCall() {
    return state.isOnCall
  },
  confirmCall() {
    return state.confirmCall
  },
  outgoingCallDetail() {
    return state.outgoingCallDetail
  },
  hasUserInteraction() {
    return state.hasUserInteraction
  },
}

export const mutations = {
  TOGGLE_FLEX_FRAME(state, value) {
    if (typeof value === 'boolean') {
      state.showFlexFrame = value
      return
    }
    state.showFlexFrame = !state.showFlexFrame
  },
  SET_FLEX_COMMAND(state, command) {
    state.flexCommand = command
  },
  SET_FLEX_COMMAND_TRIGGER(state) {
    state.flexCommandTrigger = uuid.v4()
  },
  SET_AGENT_STATUS(state, agentStatus) {
    state.agentStatus = agentStatus
  },
  SET_IS_ON_CALL(state, isOnCall) {
    state.isOnCall = isOnCall
  },
  SET_CONFIRM_CALL(state, confirmCall) {
    state.confirmCall = confirmCall
  },
  SET_OUTGOING_CALL_DETAIL(state, outgoingCallDetail) {
    state.outgoingCallDetail = outgoingCallDetail
  },
  SET_HAS_USER_INTERACTION(state, hasUserInteraction) {
    state.hasUserInteraction = Boolean(hasUserInteraction)
  },
}

export const actions = {
  setFlexWindowDisplay({ commit }, value) {
    commit('TOGGLE_FLEX_FRAME', value)
  },
  ensureFlexAudioWarning({ dispatch, rootGetters }) {
    if (!rootGetters['auth/hasCallCenter']) return
    if (state.hasUserInteraction) return
    dispatch(
      'app/showAlert',
      {
        key: FLEX_AUDIO_WARNING_KEY,
        type: 'warning',
        timeout: 0,
        dismissible: false,
        message: FLEX_AUDIO_WARNING_MESSAGE,
      },
      { root: true }
    )
  },
  handleFlexUserInteraction({ commit, dispatch }, hasUserInteraction) {
    // Flex will send a boolean `user_interaction` (or `value`) when it exists.
    // If it's explicitly `false`, show the warning. Otherwise (true or missing), clear the warning.
    if (hasUserInteraction === false) {
      commit('SET_HAS_USER_INTERACTION', false)
      dispatch('ensureFlexAudioWarning')
      return
    }

    dispatch('markFlexUserInteraction')
  },
  markFlexUserInteraction({ commit, dispatch }) {
    commit('SET_HAS_USER_INTERACTION', true)
    dispatch('app/hideAlertsByKey', FLEX_AUDIO_WARNING_KEY, { root: true })
  },
  callNumber({ commit, dispatch, rootGetters }, callDetail) {
    if (!rootGetters['auth/isFlexUser']) {
      return logger.warn('not a flex user')
    }
    if (state.agentStatus !== 'Available' || state.isOnCall) {
      const message = state.isOnCall
        ? 'You cannot make a call while on another call.'
        : 'You must be Available in Twilio to make calls.'
      return dispatch(
        'app/showAlert',
        { type: 'error', message },
        { root: true }
      )
    }
    commit('SET_CONFIRM_CALL', true)
    const firstName = callDetail?.customer?.firstName
    const lastName = callDetail?.customer?.lastName
    const phone = callDetail?.customer?.phone
    const outgoingCallDetail = {
      firstName,
      lastName,
      phone,
    }
    commit('SET_OUTGOING_CALL_DETAIL', outgoingCallDetail)
    commit('SET_FLEX_COMMAND', { action_name: 'make_call', to_number: phone })
  },
  startOutgoingCall({ commit }) {
    commit('SET_FLEX_COMMAND_TRIGGER')
  },
  clearFlexCommand({ commit }) {
    commit('SET_FLEX_COMMAND', undefined)
  },
  setConfirmCall({ commit }, confirmCall) {
    commit('SET_CONFIRM_CALL', confirmCall)
  },
  sendAgentInfo({ commit }, userId) {
    commit('SET_FLEX_COMMAND', { action_name: 'user_data', user_id: userId })
    commit('SET_FLEX_COMMAND_TRIGGER')
  },
  getAgentInfo({ commit }, userId) {
    commit('SET_FLEX_COMMAND', { action_name: 'get_worker', user_id: userId })
    commit('SET_FLEX_COMMAND_TRIGGER')
  },
  setAgentStatus({ commit }, agentStatus) {
    commit('SET_AGENT_STATUS', agentStatus)
  },
  setIsOnCall({ commit }, isOnCall) {
    commit('SET_IS_ON_CALL', isOnCall)
  },
  setHasUserInteraction({ commit }, hasUserInteraction) {
    commit('SET_HAS_USER_INTERACTION', hasUserInteraction)
  },
}
